/*
 * Bar pack selector (snippets/product-bar-packs.liquid).
 * Pack sizes shown by number of bars with per-bar price and $ savings, following the purchase type
 * (Subscribe & Save / One-time) of <purchase-options>.
 *
 * Modes:
 * - variant:  cards are the pack size option radios of <variant-selects>; the theme re-renders them on variant change.
 *             If the selected variant is hidden (e.g. "Single"), Liquid pre-checks the first visible card and this
 *             script switches the variant once on load (data-needs-sync).
 * - quantity: products without variants (Variety Pack); each card sets the form quantity (boxes).
 *
 * Public hooks (for the sticky add to cart, etc.):
 * - Event `bar-packs:change` (bubbles to document) with `detail`:
 *   { sectionId, productId, variantId, mode, key, label, quantity, bars, boxes, barsPerBox, purchaseType, sellingPlanId,
 *     price, compareAtPrice, pricePerBar, savingsAmount, savingsPercent, unitPrice }
 *   Prices in cents. `price` is the total for the selected pack (unit price × quantity), `compareAtPrice` the
 *   reference total (0 when there is no saving), `pricePerBar` = price / bars (rounded).
 * - Mirrored as data attributes on <bar-packs> and the closest <product-info>:
 *   data-bars, data-bar-pack-quantity, data-bar-pack-label, data-bar-pack-price, data-bar-pack-compare-price,
 *   data-price-per-bar, data-bar-pack-savings
 * - `element.state` returns the latest detail.
 */
(function () {
  'use strict';

  if (customElements.get('bar-packs')) return;

  const formatMoney = (cents) => {
    if (window.theme && window.theme.formatMoney) return window.theme.formatMoney(cents, window.theme.moneyFormat);
    return `$${(cents / 100).toFixed(2)}`;
  };

  const fill = (template, values) => String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  class BarPacks extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-bar-packs-data]').textContent);
      } catch (error) {
        return;
      }

      this.mode = this.data.mode;
      this.productInfo = this.closest('product-info');
      this.inputs = Array.from(this.querySelectorAll('[data-bar-pack-input]'));
      this.purchase = this.readPurchaseState();

      this.onInputChange = () => {
        if (this.mode === 'quantity') this.applyQuantity();
        this.render();
      };
      this.onPurchaseChange = (event) => {
        if (!this.isOwnEvent(event)) return;
        this.purchase = event.detail;
        // Purchase box may upgrade after this element: hand it the selected quantity
        const option = this.mode === 'quantity' ? this.getSelectedOption() : null;
        if (option && event.detail.quantity !== option.quantity) event.target.setQuantity?.(option.quantity);
        this.render();
      };

      this.inputs.forEach((input) => input.addEventListener('change', this.onInputChange));
      document.addEventListener('purchase-options:change', this.onPurchaseChange);

      if (this.mode === 'quantity' && window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
        // The theme resets the quantity to its minimum after add to cart / quantity rule updates
        this.quantityUnsubscriber = window.subscribe(window.theme.PUB_SUB_EVENTS.quantityUpdate, () => this.applyQuantity());
      }

      if (this.mode === 'quantity') this.applyQuantity();
      this.render();

      if (this.dataset.needsSync === 'true') {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.syncHiddenSelection(), {once: true});
        } else {
          this.syncHiddenSelection();
        }
      }
    }

    disconnectedCallback() {
      this.inputs?.forEach((input) => input.removeEventListener('change', this.onInputChange));
      document.removeEventListener('purchase-options:change', this.onPurchaseChange);
      this.quantityUnsubscriber?.();
    }

    get state() {
      return this.currentState || null;
    }

    get purchaseOptions() {
      return (this.productInfo || document).querySelector('purchase-options');
    }

    isOwnEvent(event) {
      if (this.productInfo) return this.productInfo.contains(event.target);
      return String(event.detail?.productId) === String(this.dataset.productId);
    }

    readPurchaseState() {
      const purchaseOptions = this.purchaseOptions;
      if (purchaseOptions?.state) return purchaseOptions.state;
      if (!purchaseOptions) return {purchaseType: 'onetime', sellingPlanId: null};

      return {
        purchaseType: this.productInfo?.dataset.purchaseType || purchaseOptions.dataset.purchaseType || 'onetime',
        sellingPlanId: this.productInfo?.dataset.sellingPlanId || purchaseOptions.dataset.sellingPlanId || null,
      };
    }

    get selectedInput() {
      return this.inputs.find((input) => input.checked) || null;
    }

    getOption(key) {
      return this.data.options.find((option) => String(option.key) === String(key)) || null;
    }

    getSelectedOption() {
      const input = this.selectedInput;
      if (!input) return null;
      return this.getOption(input.closest('[data-bar-pack-option]')?.dataset.key);
    }

    // Price of one unit of the option's variant for the current purchase type
    getPlan(option) {
      const isSubscription = this.purchase?.purchaseType === 'subscription';
      const plans = option.plans || [];
      if (!isSubscription || !plans.length) return null;
      return plans.find((plan) => String(plan.id) === String(this.purchase.sellingPlanId)) || plans[0];
    }

    compute(option) {
      const plan = this.getPlan(option);
      const quantity = option.quantity || 1;
      const unitPrice = plan ? plan.price : option.price;
      const price = unitPrice * quantity;
      const bars = option.bars || 0;
      const oneTimeTotal = option.price * quantity;
      const compareTotal = option.compareAtPrice > option.price ? option.compareAtPrice * quantity : 0;
      const baselineTotal = this.data.baselinePerBar && bars ? Math.round(this.data.baselinePerBar * bars) : 0;
      const reference = Math.max(oneTimeTotal, compareTotal, baselineTotal);
      const savingsAmount = reference > price ? reference - price : 0;

      return {
        quantity,
        bars,
        unitPrice,
        price,
        sellingPlanId: plan ? String(plan.id) : null,
        compareAtPrice: savingsAmount ? reference : 0,
        pricePerBar: bars ? Math.round(price / bars) : null,
        savingsAmount,
        savingsPercent: savingsAmount ? Math.round((savingsAmount * 100) / reference) : 0,
      };
    }

    render() {
      const labels = this.data.labels || {};

      this.querySelectorAll('[data-bar-pack-option]').forEach((element) => {
        const option = this.getOption(element.dataset.key);
        if (!option) return;

        const values = this.compute(option);
        const input = element.querySelector('[data-bar-pack-input]');
        element.classList.toggle('is-selected', Boolean(input?.checked));

        const perBar = element.querySelector('[data-bar-pack-per-bar]');
        if (perBar && values.pricePerBar !== null) perBar.textContent = fill(labels.perBar, {price: formatMoney(values.pricePerBar)});

        const savings = element.querySelector('[data-bar-pack-savings]');
        if (savings) {
          savings.hidden = !values.savingsAmount;
          savings.textContent = fill(labels.savings, {amount: formatMoney(values.savingsAmount), percent: `${values.savingsPercent}%`});
        }

        const price = element.querySelector('[data-bar-pack-price]');
        if (price) price.textContent = formatMoney(values.price);

        const compare = element.querySelector('[data-bar-pack-compare]');
        if (compare) {
          compare.hidden = !values.compareAtPrice;
          if (values.compareAtPrice) compare.textContent = formatMoney(values.compareAtPrice);
        }
      });

      this.publish();
    }

    publish() {
      const option = this.getSelectedOption();
      if (!option) return;

      const values = this.compute(option);
      const detail = {
        sectionId: this.dataset.sectionId,
        productId: this.dataset.productId,
        variantId: option.variantId ? String(option.variantId) : null,
        mode: this.mode,
        key: String(option.key),
        label: option.label,
        quantity: values.quantity,
        bars: values.bars,
        boxes: option.boxes,
        barsPerBox: this.data.barsPerBox,
        purchaseType: this.purchase?.purchaseType || 'onetime',
        sellingPlanId: values.sellingPlanId,
        unitPrice: values.unitPrice,
        price: values.price,
        compareAtPrice: values.compareAtPrice,
        pricePerBar: values.pricePerBar,
        savingsAmount: values.savingsAmount,
        savingsPercent: values.savingsPercent,
      };

      this.currentState = detail;
      [this, this.productInfo].forEach((element) => {
        if (!element) return;
        element.dataset.bars = detail.bars;
        element.dataset.barPackQuantity = detail.quantity;
        element.dataset.barPackLabel = detail.label;
        element.dataset.barPackPrice = detail.price;
        element.dataset.barPackComparePrice = detail.compareAtPrice;
        element.dataset.pricePerBar = detail.pricePerBar ?? '';
        element.dataset.barPackSavings = detail.savingsAmount;
      });

      this.dispatchEvent(new CustomEvent('bar-packs:change', {bubbles: true, detail}));
    }

    // Quantity mode: the selected card drives the form's quantity input (stepper is hidden)
    applyQuantity() {
      const option = this.getSelectedOption();
      if (!option) return;

      const quantity = String(option.quantity || 1);
      const scope = this.productInfo || document;
      const quantityInput = Array.from(scope.querySelectorAll('[data-quantity-field]')).find((input) => !input.closest('cart-bar'));

      if (quantityInput && quantityInput.value !== quantity) {
        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event('input', {bubbles: true}));
        quantityInput.dispatchEvent(new Event('change', {bubbles: true}));
      }

      this.purchaseOptions?.setQuantity?.(Number(quantity));
    }

    // Variant mode: the page loaded on a hidden variant; switch to the pre-checked visible card (once per page)
    syncHiddenSelection() {
      const input = this.selectedInput;
      if (!input) return;

      if (this.productInfo) {
        if (this.productInfo.dataset.barPacksSynced === 'true') return;
        this.productInfo.dataset.barPacksSynced = 'true';
      }

      const event = new Event('change', {bubbles: true});
      event.fromCartBar = true; // theme flag: don't move focus/scroll after the update
      input.dispatchEvent(event);
    }
  }

  customElements.define('bar-packs', BarPacks);
})();
